import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import useAuthStore from "../store/authStore";
import { useSocket } from "./SocketContext";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5001";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notificationStatus, setNotificationStatus] = useState(null);
  const { token } = useAuthStore();
  const { socket, isConnected } = useSocket();

  const refreshNotificationStatus = useCallback(async () => {
    if (!token) {
      setNotificationStatus(null);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/search/urgent`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.overdue?.length > 0) {
          setNotificationStatus("overdue");
        } else if (data.dueToday?.length > 0) {
          setNotificationStatus("today");
        } else {
          setNotificationStatus(null);
        }
      }
    } catch (err) {
      console.error("Error fetching notification status:", err);
    }
  }, [token]);

  const clearNotificationStatus = useCallback(() => {
    setNotificationStatus(null);
  }, []);

  // Listen for socket events that might affect notification status
  useEffect(() => {
    if (!socket || !isConnected || !token) return;

    // Events that might change the notification dot
    // Note: task:updated removed to avoid refreshing during description autosave
    const relevantEvents = [
      "task:created",
      "task:deleted",
      "todoList:created",
      "todoList:updated",
      "todoList:deleted",
    ];

    const handleRelevantEvent = () => {
      // Debounce slightly to avoid too many rapid refreshes
      setTimeout(() => {
        refreshNotificationStatus();
      }, 500);
    };

    // Subscribe to all relevant events
    relevantEvents.forEach((event) => {
      socket.on(event, handleRelevantEvent);
    });

    // Cleanup
    return () => {
      relevantEvents.forEach((event) => {
        socket.off(event, handleRelevantEvent);
      });
    };
  }, [socket, isConnected, token, refreshNotificationStatus]);

  // Refresh periodically (every 5 minutes)
  useEffect(() => {
    if (!token) return;

    refreshNotificationStatus();
    const interval = setInterval(refreshNotificationStatus, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, refreshNotificationStatus]);

  return (
    <NotificationContext.Provider
      value={{
        notificationStatus,
        refreshNotificationStatus,
        clearNotificationStatus,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
}

export default NotificationContext;
